import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";

interface ScrollToBottomButtonProps {
  scrollingElement?: React.RefObject<HTMLElement | null>;
}

/** 
 * A button that appears when the user has scrolled up, allowing them to quickly scroll to the bottom of the page or a specific scrolling element.
 * @param scrollingElement The element to scroll. If not provided, scrolls the window.
 */
export default function ScrollToBottomButton({
  scrollingElement
}: ScrollToBottomButtonProps) {
  // Show/hide the "scroll to bottom" button based on scroll position
  const [isScrollHidden, setIsScrollHidden] = useState("opacity-0");

  // based on the scroll position, show or hide the button "scroll to bottom" button
  useEffect(() => {
    // register the listener on either the scrollingElement or window
    // and clean up the listener on unmount
    if (scrollingElement?.current) {
      // register on the provided element
      const target = scrollingElement.current;

      const handleElemScroll = () => {
        const scrollPosition = target.scrollTop + target.clientHeight;
        const threshold = target.scrollHeight - 100; // 100px from bottom

        if (scrollPosition < threshold) {
          setIsScrollHidden("");
        } else {
          setIsScrollHidden("opacity-0");
        }
      }

      // invoke once to set initial state
      handleElemScroll();

      target.addEventListener('scroll', handleElemScroll);
      return () => { target.removeEventListener('scroll', handleElemScroll); };
    }
    else {
      // register on window
      const handleWindowScroll = () => {
        const scrollPosition = window.scrollY + window.innerHeight;
        const threshold = document.body.scrollHeight - 100; // 100px from bottom

        if (scrollPosition < threshold) {
          setIsScrollHidden("");
        } else {
          setIsScrollHidden("opacity-0");
        }
      }

      // invoke once to set initial state
      handleWindowScroll();

      window.addEventListener('scroll', handleWindowScroll);
      return () => { window.removeEventListener('scroll', handleWindowScroll); };
    }
  }, [scrollingElement?.current, setIsScrollHidden]);

  const scrollToBottom = () => {
    const target = scrollingElement?.current;
    if (target) {
      target.scrollTo({ top: target.scrollHeight, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    }
  }

  return (
      <div id="scroll-to-bottom" className={`${isScrollHidden} transition-all transition-discrete fixed bottom-8 right-8`}>
        <button
          className="rounded-full bg-gray-400 text-white h-16 w-16 text-center flex items-center justify-center hover:bg-gray-500"
          onClick={() => scrollToBottom()}
        >
          <FontAwesomeIcon icon="arrow-down" className="text-2xl" />
        </button>
      </div>
  )
}
